import type { ApiResponse, SessionView } from '../shared/contracts';

let csrfToken = '';

export class ApiClientError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly retryable: boolean,
    readonly fieldErrors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(method !== 'GET' && method !== 'HEAD' && csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
      ...init.headers,
    },
  });
  const payload = (await response.json().catch(() => null)) as ApiResponse<T> | null;
  if (!payload || !payload.ok) {
    const error = payload && !payload.ok ? payload.error : null;
    throw new ApiClientError(
      error?.code ?? 'NETWORK_RESPONSE_INVALID',
      error?.message ?? 'RepoHost received an invalid server response.',
      error?.retryable ?? response.status >= 500,
      error?.fieldErrors,
    );
  }
  return payload.data;
}

export async function loadSession(): Promise<SessionView> {
  const session = await api<SessionView>('/api/session');
  csrfToken = session.csrfToken ?? '';
  return session;
}

export function post<T>(path: string, body: unknown, headers?: HeadersInit): Promise<T> {
  return api<T>(path, {
    method: 'POST',
    body: JSON.stringify(body),
    ...(headers ? { headers } : {}),
  });
}

export function remove<T>(path: string): Promise<T> {
  return api<T>(path, { method: 'DELETE' });
}
